import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface StaticTermsModalProps {
  type: 'terms' | 'privacy' | 'cancellation';
  title: string;
  isOpen: boolean;
  onClose: () => void;
}

const legalContent = {
  terms: {
    en: {
      title: "InterviewR Terms and Conditions",
      version: "2.1",
      effectiveDate: "2025-11-08",
      content: `
      <h2>1. General Provisions</h2>
      <p>Welcome to InterviewR! By using our platform, you agree to these Terms and Conditions. Please read them carefully. InterviewR ('the Platform') provides mock interview services, including video recording, and a system for connecting job seekers with interviewers.</p>
      <p>These Terms and Conditions ('Terms') govern your access to and use of the InterviewR platform ('Platform', 'we', 'us', or 'our'). By accessing or using the Platform, you agree to be bound by these Terms.</p>
      
      <h2>2. Services</h2>
      <p>InterviewR is an online platform that connects interviewees and interviewers for mock interviews conducted online.</p>
      <p><strong>For Job Seekers:</strong> The Platform connects you with professional interviewers. You can book and receive mock interviews and access a copy of the recorded video for your review.</p>
      <p><strong>For Interviewers:</strong> The Platform allows you to offer your services for mock interviews. You will be compensated for each interview you conduct. You may also be eligible for a bonus or shared fee if a company hires a candidate you interviewed through the Platform.</p>
      
      <h2>3. Payments</h2>
      <p><strong>For Job Seekers:</strong> You must pay for all booked interviews in advance. The payment is processed securely on the Platform.</p>
      <p><strong>For Interviewers:</strong> You will be paid a pre-agreed fee per mock interview. Bonuses or shared fees will be paid as specified in your individual service agreement.</p>
      
      <h2>4. Cancellations and Refunds</h2>
      <p><strong>For Job Seekers:</strong> Cancellations must be made at least 24 hours before the scheduled interview to receive a full refund. Cancellations within 24 hours may result in partial or no refund. No-shows will not be eligible for refunds.</p>
      <p><strong>For Interviewers:</strong> In the case of a cancelled interview, you may be eligible for a cancellation fee, depending on the timing of the cancellation by the job seeker.</p>
      
      <h2>5. User Responsibilities</h2>
      <p>You are responsible for the accuracy of all information you provide on the Platform. You must not use the Platform for any illegal or unauthorized purpose. You agree to act professionally and respectfully during all interactions and to refrain from any false, misleading, or inappropriate behavior or content.</p>
      
      <h2>6. Intellectual Property</h2>
      <p>All content, including the video recordings of interviews, remains the property of InterviewR. By using the Platform, you grant InterviewR the right to store, process, and use the recordings for the purpose of service provision and platform improvement.</p>
      
      <h2>7. Limitation of Liability</h2>
      <p>InterviewR is not responsible for the outcome of any real job interviews or applications. The Platform is a tool for practice and preparation only. InterviewR is not responsible for any disputes that may arise between job seekers and interviewers.</p>
      
      <h2>8. Termination of Account</h2>
      <p>InterviewR reserves the right to terminate your account at any time and for any reason, particularly in cases of a breach of these Terms and Conditions.</p>
      
      <h2>9. Governing Law</h2>
      <p>These Terms and Conditions shall be governed by and construed in accordance with the laws of Georgia.</p>
      
      <h2>10. Changes to Terms</h2>
      <p>We may modify these Terms from time to time. Updated versions will be posted on this page.</p>
      
      <h2>11. Contact Information</h2>
      <p>For questions about these terms, contact us at: <strong>Email:</strong> info@interview-r.com, <strong>Address:</strong> Tbilisi, Georgia</p>
      <p><strong>Service Provider:</strong> InterviewR is operated by Nikoloz Turkishvili, Individual Entrepreneur, Tbilisi, Georgia.</p>
    `
    },
    ka: {
      title: "InterviewR-ის წესები და პირობები",
      version: "2.1",
      effectiveDate: "2025-11-08",
      content: `
      <h2>1. ზოგადი დებულებები</h2>
      <p>მოგესალმებით interviewR-ში! ჩვენი პლატფორმის გამოყენებით, თქვენ ეთანხმებით წინამდებარე წესებსა და პირობებს. გთხოვთ, ყურადღებით გაეცნოთ interviewR პლეტფორმის წესებსა და პირობებს. interviewR ("პლატფორმა") უზრუნველყოფს მოდელირებული ინტერვიუს სერვისებს, რაც მოიცავს ვიდეოჩანაწერების წარმოებას და სისტემას დასაქმების მაძიებელთა ინტერვიუერებთან დასაკავშირებლად.</p>
      
      <h2>2. სერვისები</h2>
      <p>interviewR ონლაინ პლატფორმაა, რომელიც ერთმანეთს აკავშირებს მოდელირებული გასაუბრების გავლის მსურველებს და ინტერვიუერებს.</p>
      <p><strong>დასაქმების მაძიებელთათვის:</strong> პლატფორმის საშუალებით თქვენ დაუკავშირდებით პროფესიონალ ინტერვიუერებს. თქვენ შეგიძლიათ დაჯავშნოთ და გაიაროთ მოდელირებული ინტერვიუები, თქვენ გექნებათ წვდომა ინტერვიუს ვიდეოჩანაწერზე, შემდგომი ანალიზისთვის.</p>
      <p><strong>ინტერვიუერებისთვის:</strong> პლატფორმა საშუალებას გაძლევთ შესთავაზოთ თქვენი სერვისები მოდელირებული ინტერვიუებისთვის. ყოველი ჩატარებული ინტერვიუსთვის თქვენ მიიღებთ კომპენსაციას. თქვენ ასევე შეგიძლიათ მიიღოთ ბონუსი ან საზიარო ანაზღაურება, თუ პლატფორმის მეშვეობით, კომპანია დაიქირავებს კანდიდატს რომელსაც გასაუბრება თქვენ ჩაუტარეთ.</p>
      
      <h2>3. გადახდები</h2>
      <p><strong>დასაქმების მაძიებელთათვის:</strong> ყველა დაჯავშნილი ინტერვიუს საფასური წინასწარ უნდა გადაიხადოთ. გადახდა პლატფორმაზე უსაფრთხოდ მუშავდება.</p>
      <p><strong>ინტერვიუერებისთვის:</strong> თქვენ მიიღებთ წინასწარ შეთანხმებულ ანაზღაურებას თითოეული იმიტირებული ინტერვიუსთვის. ბონუსების ან გაზიარებული საკომისიოს გადახდა მოხდება, როგორც ეს მითითებულია თქვენს ინდივიდუალურ მომსახურების ხელშეკრულებაში.</p>
      
      <h2>4. გაუქმება და თანხის დაბრუნება</h2>
      <p><strong>დასაქმების მაძიებელთათვის:</strong> სრული თანხის დაბრუნების მისაღებად, ვიზიტის გაუქმება უნდა განხორციელდეს დაგეგმილ გასაუბრებამდე სულ მცირე 24 საათით ადრე. 24 საათის განმავლობაში გაუქმების შემთხვევაში, შესაძლოა, თანხა ან არ დაბრუნდეს ან თანხის ნაწილობრივი დაბრუნება მოხდეს. გასაუბრებაზე არ გამოცხადების შემთხვევაში თანხა არ დაბრუნდება.</p>
      <p><strong>ინტერვიუერებისთვის:</strong> ინტერვიუს გაუქმების შემთხვევაში, თქვენ შესაძლოა მიიღოთ გაუქმების კომპენსაცია, ეს დამოკიდებილია დასაქმების მაძიებელის მხრიდან ინტერვიუს გაუქმების დროზე.</p>
      
      <h2>5. მომხმარებელის პასუხისმგებლობა</h2>
      <p>თქვენ ხართ პასუხისმგებელი პლატფორმაზე თქვენს მიერ მოწოდებული ყველა ინფორმაციის სიზუსტეზე. თქვენ არ უნდა გამოიყენოთ პლატფორმა რაიმე უკანონო ან არაავტორიზებული მიზნებისთვის. თქვენ თანახმა ხართ, რომ იმოქმედებთ პროფესიონალურად და პატივისცემით ყველა ურთიერთქმედების დროს და თავი შეიკავოთ ნებისმიერი ცრუ შინაარსის, შეცდომაში შემყვანი ან შეუსაბამო ქცევისგან.</p>
      
      <h2>6. ინტელექტუალური საკუთრება</h2>
      <p>ყველა მასალა, მათ შორის ინტერვიუების ვიდეოჩანაწერები, რჩება InterviewR-ის საკუთრებაში. პლატფორმის გამოყენებით, თქვენ ანიჭებთ InterviewR-ს უფლებას შეინახოს, დაამუშაოს და გამოიყენოს ჩანაწერები მომსახურების მიწოდებისა და პლატფორმის გაუმჯობესების მიზნით.</p>
      
      <h2>7. პასუხისმგებლობის შეზღუდვა</h2>
      <p>InterviewR არ არის პასუხისმგებელი რეალური სამუშაო გასაუბრების ან განაცხადების შედეგებზე. პლატფორმა მხოლოდ პრაქტიკისა და მომზადების ინსტრუმენტია. InterviewR არ არის პასუხისმგებელი დასაქმების მაძიებლებსა და ინტერვიუერს შორის წარმოშობილ დავებზე.</p>
      
      <h2>8. ანგარიშის გაუქმება</h2>
      <p>InterviewR უფლებას იტოვებს ნებისმიერ დროს და ნებისმიერი მიზეზით გააუქმოს თქვენი ანგარიში, განსაკუთრებით ამ წესებისა და პირობების დარღვევის შემთხვევაში.</p>
      
      <h2>9. მარეგულირებელი კანონმდებლობა</h2>
      <p>ეს წესები და პირობები რეგულირდება და განიმარტება საქართველოს კანონმდებლობის შესაბამისად.</p>
      
      <h2>10. ცვლილებები პირობებში</h2>
      <p>დროთა განმავლობაში ამ წესებსა და პირობებში შესაძლოა შევიდეს ცვლილებები. განახლებული ვერსია დაიდება ამ ვებგვერდზე.</p>
      
      <h2>11. საკონტაქტო ინფორმაცია</h2>
      <p>თუ თქვენ გაქვთ რაიმე შეკითხვა ან გადასაჭრელი საკითხი ამ პოლიტიკასთან ან თქვენს მონაცემებთან დაკავშირებით, გთხოვთ, დაგვიკავშირდეთ მისამართზე info@interview-r.com.</p>
      <p><strong>მომსახურების მიმწოდებელი:</strong> InterviewR-ს მართავს ნიკოლოზ თურქიშვილი , ინდივიდუალური მეწარმე, თბილისი, საქართველო.</p>
    `
    }
  },
  privacy: {
    en: {
      title: "InterviewR Personal Data Protection Policy",
      version: "2.1",
      effectiveDate: "2025-11-08",
      content: `
      <h2>1. Introduction</h2>
      <p>At InterviewR, we are committed to protecting your privacy. This policy explains what information we collect, how we use it, and how we keep it safe.</p>
      
      <h2>2. Information We Collect</h2>
      <p>We collect information necessary to provide our services, including:</p>
      <ul>
        <li><strong>Personal Information:</strong> Name, email address, and other contact details</li>
        <li><strong>Profile Information:</strong> Job titles, experience, and other professional details you choose to provide</li>
        <li><strong>Interview Data:</strong> Content of your mock interviews, which are video-recorded, and feedback provided by interviewers</li>
        <li><strong>Payment Information:</strong> Details related to transactions on the Platform</li>
      </ul>
      
      <h2>3. How We Use Your Information</h2>
      <p>We use the collected information to:</p>
      <ul>
        <li>Provide and improve our services</li>
        <li>Match job seekers with interviewers</li>
        <li>Process payments and issue invoices</li>
        <li>Store and make interview recordings available to you and your interviewer</li>
        <li>Analyze platform usage to improve our service offerings</li>
        <li>Share information with a potential hiring company, with your explicit consent, as part of our bonus/fee-sharing model for interviewers</li>
      </ul>
      
      <h2>4. Data Storage and Security</h2>
      <p>We use secure servers and industry-standard security measures to protect your data. Video recordings and personal data are stored securely and are only accessible by authorized personnel. All data is encrypted to ensure it is safe from unauthorized access.</p>
      
      <h2>5. Data Sharing</h2>
      <p>We do not sell your personal data. Your personal data, including interview recordings, will only be shared with third parties with your explicit consent. For example, if you and your interviewer agree to be part of the bonus/fee-sharing program, your data may be shared with a potential hiring company.</p>
      
      <h2>6. Your Rights</h2>
      <p>You have the right to access and correct your personal information. You can request the deletion of your account and associated data. You can withdraw your consent for data processing at any time.</p>
      
      <h2>7. Data Retention</h2>
      <p>Personal data is retained while your account is active and automatically deleted 30 days after account closure. Interview recordings are stored for 90 days maximum unless you request immediate deletion.</p>
      
      <h2>8. Contact Us</h2>
      <p>If you have any questions or concerns about this policy or your data, please contact us at <strong>info@interview-r.com</strong>.</p>
    `
    },
    ka: {
      title: "InterviewR პერსონალური მონაცემთა დაცვის პოლიტიკა",
      version: "2.1",
      effectiveDate: "2025-11-08",
      content: `
      <h2>1. შესავალი</h2>
      <p>InterviewR-ში ვიცავთ თქვენს პერსონალურ მონაცემებს. ეს პირობები განმარტავს თუ რა ინფორმაციას ვაგროვებთ, როგორ ვიყენებთ და როგორ ვინახავთ მას უსაფრთხოდ.</p>
      
      <h2>2. ჩვენს მიერ შეგროვებული ინფორმაცია</h2>
      <p>ჩვენ ვაგროვებთ ჩვენი მომსახურების მიწოდებისთვის აუცილებელ ინფორმაციას, მათ შორის:</p>
      <ul>
        <li><strong>პერსონალური ინფორმაცია:</strong> სახელი, ელექტრონული ფოსტის მისამართი და სხვა საკონტაქტო ინფორმაცია</li>
        <li><strong>პროფილის ინფორმაცია:</strong> სამსახურეობრივი პოზიციები, გამოცდილება და სხვა პროფესიული დეტალები, რომელთა მიწოდებასაც თქვენ აირჩევთ</li>
        <li><strong>ინტერვიუს მონაცემები:</strong> თქვენი მოდელირებული ინტერვიუს მონაცემებს და შინაარსს, რომლის ვიდეოჩაწერა ხდება და რომელზეც ინტერვიუერები იძლევიან უკუკავშირს</li>
        <li><strong>გადახდის ინფორმაცია:</strong> პლატფორმაზე ტრანზაქციებთან დაკავშირებული დეტალები</li>
      </ul>
      
      <h2>3. როგორ ვიყენებთ თქვენს ინფორმაციას</h2>
      <p>ჩვენ ვიყენებთ შეგროვებულ ინფორმაციას შემდეგი მიზნებისთვის:</p>
      <ul>
        <li>ჩვენი მომსახურების უზრუნველსაყოფად და გასაუმჯობესებლად</li>
        <li>დასაქმების მაძიებელთა ინტერვიუერებთან დასაკავშირებლად</li>
        <li>გადახდების დასამუშავებლად და ინვოისების გასაცემად</li>
        <li>ინტერვიუს ჩანაწერების გასაკეთებლად და შესანახად, რაც ხელმისაწვდომი იქნება თქვენთვის და თქვენი ინტერვიუერისთვის</li>
        <li>პლატფორმის გამოყენების ანალიზისთვის ჩვენი მომსახურების გასაუმჯობესებლად</li>
        <li>ინფორმაციის გასაზიარებლად პოტენციურ დამსაქმებელ კომპანიასთან, თქვენი თანხმობით, ინტერვიუერებისთვის განკუთვნილი ჩვენი ბონუსების/საკომისიოს გაზიარების მოდელის ფარგლებში</li>
      </ul>
      
      <h2>4. მონაცემთა შენახვა და უსაფრთხოება</h2>
      <p>თქვენი მონაცემების დასაცავად ჩვენ ვიყენებთ დაცულ სერვერებს და ინდუსტრიის სტანდარტების შესაბამის უსაფრთხოების ზომებს. ვიდეოჩანაწერები და პერსონალური მონაცემები უსაფრთხოდ ინახება და მათზე წვდომა მხოლოდ უფლებამოსილი პერსონალისთვისაა. ყველა მონაცემი დაშიფრულია, რათა უზრუნველყოფილი იყოს მათი უსაფრთხოება არაავტორიზებული წვდომისგან.</p>
      
      <h2>5. მონაცემთა გაზიარება</h2>
      <p>ჩვენ არ ვყიდით თქვენს პერსონალურ მონაცემებს. თქვენი პერსონალური მონაცემები, მათ შორის ინტერვიუს ჩანაწერები, მესამე მხარეებთან გაზიარდება მხოლოდ თქვენი თანხმობის შემთხვევაში. მაგალითად, თუ თქვენ და თქვენი ინტერვიუერი დათანხმდებით ბონუსების/ჰონორარის გაზიარების პროგრამაში მონაწილეობას, თქვენი მონაცემები შეიძლება გაზიარდეს პოტენციურ დამსაქმებელ კომპანიასთან.</p>
      
      <h2>6. თქვენი უფლებები</h2>
      <p>თქვენ გაქვთ უფლება, მიიღოთ წვდომა თქვენს პერსონალურ ინფორმაციაზე და შეასწოროთ იგი. შეგიძლიათ მოითხოვოთ თქვენი ანგარიშის და მასთან დაკავშირებული მონაცემების წაშლა. შეგიძლიათ ნებისმიერ დროს გააუქმოთ თქვენი თანხმობა მონაცემთა დამუშავებაზე.</p>
      
      <h2>7. მონაცემთა შენახვა</h2>
      <p>პერსონალური მონაცემები ინახება თქვენი ანგარიშის აქტიურობის პერიოდში და ავტომატურად იშლება ანგარიშის დახურვიდან 30 დღის შემდეგ. ინტერვიუს ჩანაწერები ინახება მაქსიმუმ 90 დღის განმავლობაში, თუ თქვენ არ მოითხოვთ დაუყოვნებლივ წაშლას.</p>
      
      <h2>8. საკონტაქტო ინფორმაცია</h2>
      <p>თუ თქვენ გაქვთ რაიმე შეკითხვა ან გადასაჭრელი საკითხი ამ პოლიტიკასთან ან თქვენს მონაცემებთან დაკავშირებით, გთხოვთ, დაგვიკავშირდეთ მისამართზე info@interview-r.com.</p>
    `
    }
  },
  cancellation: {
    en: {
      title: "Cancellation Policy",
      version: "1.1",
      effectiveDate: "2025-11-08",
      content: `
      <h2>1. Cancellation Policy</h2>
      <p>You may cancel your scheduled interview session according to the following terms:</p>
      
      <h2>2. Cancellation Timeframes</h2>
      <ul>
        <li><strong>24+ hours before:</strong> Full refund (100%)</li>
        <li><strong>12-24 hours before:</strong> 50% refund</li>
        <li><strong>Less than 12 hours:</strong> No refund</li>
        <li><strong>No-show:</strong> No refund</li>
      </ul>
      
      <h2>3. How to Cancel</h2>
      <p>To cancel your interview:</p>
      <ul>
        <li>Log into your account</li>
        <li>Navigate to "My Bookings"</li>
        <li>Select the interview you wish to cancel</li>
        <li>Click "Cancel Booking"</li>
      </ul>
      
      <h2>4. Refund Processing</h2>
      <p>Approved refunds will be processed within 5-7 business days to your original payment method.</p>
      
      <h2>5. Interviewer Cancellations</h2>
      <p>If an interviewer cancels your session, you will receive a full refund or the option to reschedule at no additional cost.</p>
      
      <h2>6. Technical Issues</h2>
      <p>If technical issues prevent your interview from taking place, we will work with you to reschedule or provide a full refund.</p>
      
      <h2>7. Emergency Situations</h2>
      <p>In case of emergencies, please contact our support team. We will review each case individually and may waive standard cancellation fees.</p>
      
      <h2>8. Contact Support</h2>
      <p>For cancellation requests or questions, contact us at info@interview-r.com or through your account dashboard.</p>
    `
    },
    ka: {
      title: "გაუქმების პოლიტიკა",
      version: "1.1",
      effectiveDate: "2025-11-08",
      content: `
      <h2>1. გაუქმების პოლიტიკა</h2>
      <p>თქვენ შეგიძლიათ გააუქმოთ ინტერვიუს ჯავშანი შემდეგი პირობების შესაბამისად:</p>
      
      <h2>2. გაუქმების ვადები</h2>
      <ul>
        <li><strong>24+ საათით ადრე:</strong> სრული ანაზღაურება (100%)</li>
        <li><strong>12-24 საათით ადრე:</strong> 50%-იანი ანაზღაურება</li>
        <li><strong>12 საათზე ნაკლები:</strong> ანაზღაურება არ ხდება</li>
        <li><strong>არ გამოცხადების შემთხვევაში:</strong> ანაზღაურება არ ხდება</li>
      </ul>
      
      <h2>3. როგორ ხდება გაუქმება</h2>
      <p>ინტერვიუს გასაუქმებლად:</p>
      <ul>
        <li>შედით თქვენს ანგარიშზე</li>
        <li>გადაგით "My Bookings" - "ჩემს ჯავშნებზე"</li>
        <li>აირჩიეთ ინტერვიუ რომლის გაუქმებაც გსურთ</li>
        <li>დააჭირეთ "Cancel Booking" - "ჯავშნის გაუქმებას"</li>
      </ul>
      
      <h2>4. თანხის დაბრუნების დამუშავება</h2>
      <p>დადასტურების შემთხვევაში თანხის დაბრუნების დამუშავება ხდება 5-7 სამუშაო დღის განმავლობაში თქვენს თავდაპირველ გადახდის მეთოდზე.</p>
      
      <h2>5. ინტერვიუერის მიერ ჯავშნის გაუქმება</h2>
      <p>თუ ინტერვიუერი გააუქმებს თქვენს სესიას, თქვენ მიიღებთ სრულ ანაზღაურებას ან სხვა თარიღზე გადატანის შესაძლებლობას დამატებითი ხარჯების გარეშე.</p>
      
      <h2>6. ტექნიკური პრობლემები</h2>
      <p>თუ ტექნიკური პრობლემები ხელს შეუშლის თქვენი ინტერვიუს ჩატარებას, ჩვენ ვიმუშავებთ თქვენთან ინტერვიუს გადაგებაზე ან სრულ ანაზღაურებაზე.</p>
      
      <h2>7. საგანგებო სიტუაციები</h2>
      <p>საგანგებო სიტუაციების შემთხვევაში, გთხოვთ, დაუკავშირდეთ ჩვენს მხარდაჭეროს გუნდს. ჩვენ განვიხილავთ თითოეულ შემთხვევას ინდივიდუალურად და შესაძლოა უარი ვთქვათ სტანდარტული გაუქმების საკომისიოზე.</p>
      
      <h2>8. დაუკავშირდით მხარდაჭეროს სამსახურს</h2>
      <p>გაუქმების მოთხოვნის ან შეკითხვების შემთხვევაში, დაგვიკავშირდით info@interview-r.com-ზე ან თქვენი ანგარიშის დაფის მეშვეობით.</p>
    `
    }
  }
};

export function StaticTermsModal({ type, title: _title, isOpen, onClose }: StaticTermsModalProps) {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState<'en' | 'ka'>(i18n.language === 'ka' ? 'ka' : 'en');

  if (!isOpen) return null;

  const content = legalContent[type][language];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        
        <div className="relative z-50 flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-stitch-surface shadow-2xl">
          {/* Header */}
          <div className="relative shrink-0 bg-gradient-to-r from-stitch-primary to-stitch-accent px-6 py-4 text-white">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-stitch-muted transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            
            {/* Language Selector */}
            <div className="absolute top-4 left-6 flex gap-2">
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  language === 'en' 
                    ? 'bg-stitch-surface text-stitch-primary'
                    : 'bg-stitch-primary text-white hover:bg-stitch-primary-hover'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('ka')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  language === 'ka' 
                    ? 'bg-stitch-surface text-stitch-primary'
                    : 'bg-stitch-primary text-white hover:bg-stitch-primary-hover'
                }`}
              >
                KA
              </button>
            </div>
            
            <h2 className="text-2xl font-bold mt-8">{content.title}</h2>
            <p className="text-stitch-surface/85">
              Version {content.version} - Effective {new Date(content.effectiveDate).toLocaleDateString()}
            </p>
          </div>

          {/* Content */}
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <div className="prose prose-blue max-w-none">
              <div
                className="text-stitch-fg leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: content.content
                }}
                style={{
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-stitch-border px-6 py-4 bg-stitch-surface-2">
            <button
              onClick={onClose}
              className="w-full rounded-stitch-lg bg-stitch-primary py-3 font-medium text-white transition-colors hover:bg-stitch-primary-hover"
            >
              {language === 'ka' ? 'დახურვა' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
